import { useState, useEffect, useMemo } from 'react';
import DashboardLayout from '../../components/feature/DashboardLayout';
import { useAuth } from '../../lib/api/auth';
import { useFamilyData } from '../../lib/context/FamilyDataContext';
import { recordsApi } from '../../lib/api/endpoints';
import type { PointRecordInfo } from '../../lib/api/types';

// 记录类型映射
const RECORD_TYPE_REWARD = 1;
const RECORD_TYPE_PUNISH = 2;
const RECORD_TYPE_REDEEM = 3;

const channelConfig: Record<string, { bg: string; text: string; icon: string }> = {
  'AI': { bg: 'bg-violet-100', text: 'text-violet-700', icon: 'ri-robot-2-fill' },
  'H5': { bg: 'bg-sky-100', text: 'text-sky-700', icon: 'ri-smartphone-line' },
  'PC': { bg: 'bg-slate-100', text: 'text-slate-600', icon: 'ri-computer-line' },
  'pc': { bg: 'bg-slate-100', text: 'text-slate-600', icon: 'ri-computer-line' },
  '小程序': { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: 'ri-wechat-fill' },
  '系统': { bg: 'bg-gray-100', text: 'text-gray-600', icon: 'ri-settings-3-line' }
};

// 头像颜色配置
const avatarColors = [
  '#EA580C', '#EC4899', '#8B5CF6', '#06B6D4', '#10B981', '#F59E0B'
];

const getTypeConfig = (recordType: number) => {
  switch (recordType) {
    case RECORD_TYPE_REWARD:
      return { bg: 'bg-emerald-100', text: 'text-emerald-700', label: '奖励', dotColor: 'bg-emerald-400' };
    case RECORD_TYPE_PUNISH:
      return { bg: 'bg-red-100', text: 'text-red-600', label: '惩罚', dotColor: 'bg-red-400' };
    case RECORD_TYPE_REDEEM:
      return { bg: 'bg-amber-100', text: 'text-amber-700', label: '兑换', dotColor: 'bg-amber-400' };
    default:
      return { bg: 'bg-gray-100', text: 'text-gray-600', label: '其他', dotColor: 'bg-gray-400' };
  }
};

type FilterType = 'all' | 'reward' | 'punish' | 'redeem';

const PAGE_SIZE = 20;

export default function RecordsPage() {
  const { currentFamily } = useAuth();
  const { children } = useFamilyData(); // 从共享Context获取children
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<PointRecordInfo[]>([]);

  const [filterType, setFilterType] = useState<FilterType>('all');
  const [filterChild, setFilterChild] = useState<string>('all');
  const [filterChannel, setFilterChannel] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const [currentPage, setCurrentPage] = useState(1);

  // 加载记录数据（children从Context获取）
  const loadData = async () => {
    if (!currentFamily?.id) return;

    setLoading(true);
    try {
      const recordsRes = await recordsApi.getPointRecords(currentFamily.id);
      if (recordsRes.code === 0 && recordsRes.data) {
        setRecords(recordsRes.data);
      }
    } catch (error) {
      console.error('Failed to load records data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [currentFamily?.id]);

  // 格式化记录数据
  const formattedRecords = useMemo(() => {
    return records.map((r, index) => {
      const child = children.find(c => c.id === r.child_id);
      const date = new Date(r.created_at);
      // 根据child_id获取颜色索引
      const colorIndex = child ? parseInt(child.id.slice(-1)) % avatarColors.length : index % avatarColors.length;

      // 确定类型和规则名
      let recordType: string;
      let ruleName: string;

      if (r.record_type === RECORD_TYPE_REWARD) {
        recordType = 'reward';
        ruleName = r.rule_name || '奖励';
      } else if (r.record_type === RECORD_TYPE_PUNISH) {
        recordType = 'punishment';
        ruleName = r.rule_name || '惩罚';
      } else if (r.record_type === 3) {
        recordType = 'redeem';
        ruleName = r.rule_name || '兑换';
      } else if (r.record_type === 4) {
        recordType = 'deposit';
        ruleName = '充值';
      } else if (r.record_type === 5) {
        recordType = 'withdraw';
        ruleName = '提现';
      } else {
        recordType = 'other';
        ruleName = r.rule_name || '记录';
      }

      return {
        ...r,
        childName: r.child_name || child?.name || '未知',
        childAvatar: r.child_avatar || child?.avatar_url,
        childAvatarBg: avatarColors[colorIndex],
        type: recordType,
        ruleName: ruleName,
        note: r.remark,
        channel: r.source || '系统',
        time: `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`,
        date: `${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`,
        fullDate: date
      };
    });
  }, [records, children]);

  // 过滤记录
  const filtered = useMemo(() => {
    return formattedRecords.filter(r => {
      if (filterType !== 'all') {
        if (filterType === 'reward' && r.record_type !== RECORD_TYPE_REWARD) return false;
        if (filterType === 'punish' && r.record_type !== RECORD_TYPE_PUNISH) return false;
        if (filterType === 'redeem' && r.record_type !== RECORD_TYPE_REDEEM) return false;
      }
      if (filterChild !== 'all' && r.child_id !== filterChild) return false;
      if (filterChannel !== 'all') {
        const recordChannel = (r.source || '系统');
        // 支持大小写匹配，比如H5和h5都能匹配
        if (recordChannel.toLowerCase() !== filterChannel.toLowerCase()) return false;
      }
      // 日期过滤
      if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        if (r.fullDate < start) return false;
      }
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (r.fullDate > end) return false;
      }
      return true;
    });
  }, [formattedRecords, filterType, filterChild, filterChannel, startDate, endDate]);

  // 分页数据
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginatedRecords = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, currentPage]);

  // 重置页码当过滤条件变化时
  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, filterChild, filterChannel, startDate, endDate]);

  const typeFilterConfig: Record<FilterType, { label: string }> = {
    all: { label: '全部' },
    reward: { label: '奖励' },
    punish: { label: '惩罚' },
    redeem: { label: '兑换' }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-4 md:p-6 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="ri-loader-4-line animate-spin text-4xl text-blue-500"></i>
            <p className="text-gray-400 mt-2">加载中...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout onRefresh={loadData}>
      <div className="p-4 md:p-6">
        <div className="flex items-center gap-3 mb-5">
          <div>
            <h1 className="text-xl font-black text-blue-900">记录清单</h1>
            <p className="text-gray-400 text-sm mt-0.5">全部奖惩积分兑换记录</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl p-4 mb-4 border border-blue-100">
          <div className="flex flex-wrap gap-3 items-center">
            {/* 日期过滤 */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">起始日期:</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-2 py-1 text-xs border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">结束日期:</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-2 py-1 text-xs border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </div>
            {(startDate || endDate) && (
              <button
                onClick={() => { setStartDate(''); setEndDate(''); }}
                className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                清除日期
              </button>
            )}

            <div className="flex gap-1 bg-blue-50 p-1 rounded-xl">
              {(Object.keys(typeFilterConfig) as FilterType[]).map(t => (
                <button key={t} onClick={() => setFilterType(t)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    filterType === t ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {typeFilterConfig[t].label}
                </button>
              ))}
            </div>
            <div className="flex gap-1 bg-blue-50 p-1 rounded-xl">
              <button onClick={() => setFilterChild('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${filterChild === 'all' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500'}`}
              >全部孩子</button>
              {children.map(c => (
                <button key={c.id} onClick={() => setFilterChild(c.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${filterChild === c.id ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500'}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
            <div className="flex gap-1 bg-blue-50 p-1 rounded-xl">
              {['all', 'AI', 'PC', 'H5', '小程序'].map(ch => (
                <button key={ch} onClick={() => setFilterChannel(ch)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${filterChannel === ch ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500'}`}
                >
                  {ch === 'all' ? '全渠道' : ch}
                </button>
              ))}
            </div>
          </div>

          <p className="text-xs text-gray-400 mt-2">共 {filtered.length} 条记录</p>
        </div>

        {/* Records list */}
        <div className="bg-white rounded-2xl p-4 border border-blue-100">
          {paginatedRecords.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 flex items-center justify-center bg-blue-50 rounded-2xl mx-auto mb-3">
                <i className="ri-inbox-2-line text-3xl text-blue-200"></i>
              </div>
              <p className="text-gray-400 text-sm">暂无符合条件的记录</p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {paginatedRecords.map(rec => {
                  const ch = channelConfig[rec.channel] || channelConfig['系统'];
                  const tc = getTypeConfig(rec.record_type);
                  return (
                    <div key={rec.id} className="flex items-center gap-3 p-3 bg-blue-50/40 rounded-xl hover:bg-blue-50 transition-colors">
                      <div className="relative flex-shrink-0">
                        {rec.childAvatar ? (
                          <img src={rec.childAvatar} alt={rec.childName} className="w-9 h-9 rounded-xl object-cover object-top border-2 border-blue-100" />
                        ) : (
                          <div className="w-9 h-9 rounded-xl border-2 border-blue-100 flex items-center justify-center text-white font-bold" style={{ backgroundColor: rec.childAvatarBg || '#3B82F6' }}>
                            {(rec.childNickname || rec.childName || '?').charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                          <span className="text-xs font-bold text-blue-900">{rec.childName}</span>
                          <span className="text-xs text-gray-500 truncate max-w-[100px]">{rec.ruleName}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${tc.bg} ${tc.text}`}>{tc.label}</span>
                          <span className={`flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${ch.bg} ${ch.text}`}>
                            <i className={`${ch.icon} text-[9px]`}></i>{rec.channel}
                          </span>
                        </div>
                        {rec.note && <p className="text-[10px] text-gray-400 truncate">{rec.note}</p>}
                      </div>
                      <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                        <span className={`font-black text-sm ${rec.points > 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                          {rec.points > 0 ? `+${rec.points}` : rec.points}
                        </span>
                        <div className="text-[10px] text-gray-400 whitespace-nowrap">{rec.date} {rec.time}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 分页 */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-blue-100">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                      currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                        currentPage === page ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                      currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
